import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { requestTypeSettingsApi } from '../../lib/api';

export function SignatureRoundsConfig({ requestType }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowRounds, setAllowRounds] = useState(true);
  const [allowResign, setAllowResign] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await requestTypeSettingsApi.getSignatureRoundsConfig(requestType);
      setAllowRounds(res.data.allow_additional_rounds !== false);
      setAllowResign(res.data.allow_resign_new_version !== false);
    } catch {
      toast.error('Failed to load signature rounds configuration');
    } finally {
      setLoading(false);
    }
  }, [requestType]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await requestTypeSettingsApi.updateSignatureRoundsConfig(requestType, {
        allow_additional_rounds: allowRounds,
        allow_resign_new_version: allowResign,
      });
      toast.success('Signature rounds configuration saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-500 py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="space-y-4" data-testid={`signature-rounds-config-${requestType}`}>
      <p className="text-sm text-slate-500 max-w-xl">
        Control whether the Chairman can run additional e-signature rounds on {requestType} requests
        after the initial round is fully signed, and whether documents may be re-signed when a new
        version is uploaded.
      </p>
      <div className="space-y-3 max-w-xl">
        <div className="flex items-start justify-between gap-4 p-3 rounded border border-slate-200 bg-white">
          <div>
            <Label className="text-sm font-semibold text-slate-700">Allow additional signature rounds after completion</Label>
            <p className="text-xs text-slate-500 mt-0.5">
              When all signatures are collected (Ready to Execute / Executed), a "Start New Signature Round"
              action lets the Chairman send newly approved documents through e-signature without reopening
              the whole request.
            </p>
          </div>
          <Switch
            checked={allowRounds}
            onCheckedChange={setAllowRounds}
            data-testid="signature-rounds-allow-rounds-switch"
          />
        </div>
        <div className="flex items-start justify-between gap-4 p-3 rounded border border-slate-200 bg-white">
          <div>
            <Label className="text-sm font-semibold text-slate-700">Allow re-signing a document when a new version is uploaded</Label>
            <p className="text-xs text-slate-500 mt-0.5">
              When enabled, a new version of an already-signed document can be selected for e-signature
              again (in an edit or a new round). When disabled, signed document types stay locked.
            </p>
          </div>
          <Switch
            checked={allowResign}
            onCheckedChange={setAllowResign}
            data-testid="signature-rounds-allow-resign-switch"
          />
        </div>
      </div>
      <Button onClick={handleSave} disabled={saving} className="bg-blue-900 hover:bg-blue-950" data-testid="signature-rounds-save-btn">
        {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
      </Button>
    </div>
  );
}

export default SignatureRoundsConfig;
